import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import {
  CheckboxGroup as CheckboxGroupRAC,
  Checkbox as CheckboxRAC,
  TextField as TextFieldRAC,
  Input as InputRAC,
} from 'react-aria-components';
import { debounce } from 'ts-debounce';

import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { applyInternalProps, findChildOfType } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';
import { FieldsetHeadless } from '../fieldset/fieldset-headless';

import { type CheckboxGroupFieldProps } from './checkbox-group-field-props';
import {
  getIsOtherOptionSelected,
  getOptionsValues,
  getOtherLabel,
  getOtherValue,
  OTHER_ID,
} from './checkbox-group-field-utils';

export type CheckboxGroupProps = {
  children: ReactNode;
};

export const CheckboxGroup = ({ children }: CheckboxGroupProps) => {
  const props = useFieldPropsV2();
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    hasError,
    FieldLayout,
  } = props as CheckboxGroupFieldProps;

  const { inputId, labelId, ariaDescribedBy } = useFieldAttributes();

  const labelElement = findChildOfType(children, CheckboxGroup.Label);
  const optionsElement = findChildOfType(children, CheckboxGroup.Options);
  const descriptionElement = findChildOfType(
    children,
    CheckboxGroup.Description,
  );
  const errorElement = findChildOfType(children, CheckboxGroup.Error);

  const _value = useMemo(() => value || [], [value]);

  const handleChange = useCallback(
    (newValue: string[]) => {
      onChange?.(newValue);
    },
    [onChange],
  );

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <FieldsetHeadless
          id={inputId}
          ariaLabelledBy={labelId}
          ariaDescribedBy={ariaDescribedBy}
          required={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          <CheckboxGroupRAC
            value={_value}
            onChange={handleChange}
            isDisabled={disabled}
            isRequired={required}
            isInvalid={hasError}
          >
            {optionsElement}
          </CheckboxGroupRAC>
        </FieldsetHeadless>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

const Options = ({ children, className }: OptionsProps) => {
  const props = useFieldPropsV2();
  const { id, options, addOther, addOtherLabel, numberOfColumns } =
    props as CheckboxGroupFieldProps;
  const { t } = useTranslation();

  const optionElement = findChildOfType(children, Options.Option);

  const otherLabel = getOtherLabel(addOtherLabel, t);

  return (
    <div className={className} data-columns={numberOfColumns}>
      {options?.map((option, index) => {
        const checkboxValue = option.value ?? option.label;
        return applyInternalProps<OptionInternalProps>(optionElement, {
          key: option.id || option.label,
          value: checkboxValue,
          label: option.label,
          isFirst: index === 0,
          fieldId: id,
        });
      })}
      {addOther &&
        applyInternalProps<OptionInternalProps>(optionElement, {
          key: OTHER_ID,
          value: OTHER_ID,
          label: otherLabel,
          isOther: true,
        })}
    </div>
  );
};

const OptionInput = ({ className }: { className?: string }) => {
  return <div aria-hidden="true" className={className} />;
};

type OptionLabelInternalProps = {
  label: string;
};

type OptionLabelProps = {
  className?: string;
};

const OptionLabel = ({ className, ...internal }: OptionLabelProps) => {
  const { label } = internal as OptionLabelInternalProps;
  return <span className={className}>{label}</span>;
};

type CheckboxProps = {
  children: ReactNode;
  className?: string;
};

type CheckboxInternalProps = {
  value: string;
  label: string;
  isFirst?: boolean;
  isOther?: boolean;
  fieldId?: string;
};

const Checkbox = ({ children, className, ...internal }: CheckboxProps) => {
  const {
    value,
    label,
    isFirst = false,
    isOther = false,
    fieldId,
  } = internal as CheckboxInternalProps;
  const props = useFieldPropsV2();
  const {
    value: fieldValue,
    onChange,
    disabled,
    hasError,
    options,
    addOtherLabel,
  } = props as CheckboxGroupFieldProps;

  const checkboxRef = useRef<HTMLLabelElement>(null);

  useFocusFieldEvent(() => {
    if (isFirst) {
      checkboxRef.current?.focus({ preventScroll: true });
    }
  }, fieldId);

  const labelElement = findChildOfType(children, Checkbox.Label);
  const inputElement = findChildOfType(children, Checkbox.Input);

  const _value = useMemo(() => fieldValue || [], [fieldValue]);

  const actualValue = useMemo(() => {
    if (isOther) {
      return (
        _value.find(
          (val) => val === label || val.startsWith(`${addOtherLabel}: `),
        ) || label
      );
    }
    return value;
  }, [isOther, _value, label, addOtherLabel, value]);

  const handleOtherCheckboxChange = useCallback(
    (isSelected: boolean) => {
      if (!isOther) {
        return;
      }

      const optionsValues = getOptionsValues(options || []);
      const removedOther = _value.filter((val: any) =>
        optionsValues.includes(val),
      );
      const newValue = isSelected ? [...removedOther, label] : removedOther;
      onChange?.(newValue);
    },
    [isOther, label, onChange, options, _value],
  );

  return (
    <CheckboxRAC
      ref={checkboxRef}
      value={actualValue}
      onChange={isOther ? handleOtherCheckboxChange : undefined}
      isDisabled={disabled}
      isInvalid={hasError}
      className={className}
      data-hook={`checkbox-${label}`}
    >
      {inputElement}
      {applyInternalProps<OptionLabelInternalProps>(labelElement, {
        label,
      })}
    </CheckboxRAC>
  );
};

Checkbox.Label = OptionLabel;
Checkbox.Input = OptionInput;

type OptionProps = {
  children: ReactNode;
};

type OptionInternalProps = {
  value: string;
  label: string;
  isFirst?: boolean;
  isOther?: boolean;
  fieldId?: string;
};

const Option = ({ children, ...internal }: OptionProps) => {
  const {
    value,
    label,
    isFirst = false,
    isOther = false,
    fieldId,
  } = internal as OptionInternalProps;
  const props = useFieldPropsV2();
  const {
    value: fieldValue,
    onChange,
    options,
    addOtherLabel,
    addOtherPlaceholder,
    otherInputCharacterLimit,
    disabled,
    hasError,
  } = props as CheckboxGroupFieldProps;

  const checkboxElement = findChildOfType(children, Checkbox);
  const otherInputElement = findChildOfType(children, OtherInput);

  const _value = useMemo(() => fieldValue || [], [fieldValue]);

  const isOtherSelected = useMemo(() => {
    if (!isOther) {
      return false;
    }
    const optionsValues = getOptionsValues(options || []);
    return getIsOtherOptionSelected(_value, optionsValues);
  }, [isOther, _value, options]);

  return (
    <>
      {applyInternalProps<CheckboxInternalProps>(checkboxElement, {
        value,
        label,
        isFirst,
        isOther,
        fieldId,
      })}
      {isOther &&
        isOtherSelected &&
        applyInternalProps<OtherInputInternalProps>(otherInputElement, {
          fieldValue: _value,
          onChange,
          label,
          placeholder: addOtherPlaceholder,
          maxLength: otherInputCharacterLimit,
          disabled,
          hasError,
          ariaLabel: label,
          addOtherLabel,
          options,
        })}
    </>
  );
};

type OtherInputProps = {
  className?: string;
};

type OtherInputInternalProps = {
  fieldValue: string[];
  onChange: (value: string[]) => void;
  label: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  hasError?: boolean;
  ariaLabel?: string;
  addOtherLabel?: string;
  options: CheckboxGroupFieldProps['options'];
};

const OtherInput = ({ className, ...internal }: OtherInputProps) => {
  const {
    fieldValue,
    onChange,
    label,
    placeholder,
    maxLength,
    disabled,
    hasError,
    ariaLabel,
    addOtherLabel,
    options,
  } = internal as OtherInputInternalProps;

  const [otherValue, setOtherValue] = useState(() => {
    const optionsValues = getOptionsValues(options || []);
    const isSelected = getIsOtherOptionSelected(fieldValue, optionsValues);
    return isSelected ? getOtherValue(fieldValue, addOtherLabel) : '';
  });

  useEffect(() => {
    const optionsValues = getOptionsValues(options || []);
    const isSelected = getIsOtherOptionSelected(fieldValue, optionsValues);
    setOtherValue(isSelected ? getOtherValue(fieldValue, addOtherLabel) : '');
  }, [fieldValue, options, addOtherLabel]);

  const debouncedHandleChange = useMemo(() => {
    return debounce((input: string) => {
      const optionsValues = getOptionsValues(options || []);
      const removedOther = fieldValue.filter((val: any) =>
        optionsValues.includes(val),
      );
      const _otherValue = input ? `${addOtherLabel}: ${input}` : label;
      const newValue = [...removedOther, _otherValue];
      onChange(newValue);
    }, 500);
  }, [onChange, options, fieldValue, addOtherLabel, label]);

  const handleInputChange = useCallback(
    (input: string) => {
      setOtherValue(input);
      debouncedHandleChange(input);
    },
    [debouncedHandleChange],
  );

  return (
    <TextFieldRAC
      value={otherValue}
      onChange={handleInputChange}
      isDisabled={disabled}
      isInvalid={hasError}
      className={className}
    >
      <InputRAC
        placeholder={placeholder}
        maxLength={maxLength}
        aria-label={ariaLabel}
      />
    </TextFieldRAC>
  );
};

CheckboxGroup.Label = Label;
CheckboxGroup.Options = Options;
Options.Option = Option;
Option.Checkbox = Checkbox;
Option.OtherInput = OtherInput;
CheckboxGroup.Description = Description;
CheckboxGroup.Error = Error;
